import React, { useEffect, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Logo } from './Logo';
import { headerNavLeft, headerNavRight } from '../data/content';
import { useLanguage } from '../contexts/LanguageContext';
type HeaderProps = {
  onOpenMenu: () => void;
};
export function Header({
  onOpenMenu
}: HeaderProps) {
  // hidden: slid out of view while scrolling down
  // solid: white underlay + black text (only once revealed by scrolling up)
  const [hidden, setHidden] = useState(false);
  const [solid, setSolid] = useState(false);
  useEffect(() => {
    let frame = 0;
    let lastY = window.scrollY;
    const TOP = 40;
    const TOLERANCE = 6;
    const update = () => {
      frame = 0;
      const y = Math.max(0, window.scrollY);
      const delta = y - lastY;
      if (y <= TOP) {
        setHidden(false);
        setSolid(false);
        lastY = y;
        return;
      }
      if (Math.abs(delta) < TOLERANCE) return;
      if (delta > 0) {
        setHidden(true);
      } else {
        setHidden(false);
        setSolid(true);
      }
      lastY = y;
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);
  const reduceMotion = useReducedMotion();
  const {
    t
  } = useLanguage();
  const linkClass = 'whitespace-nowrap text-body transition-opacity duration-150 hover:opacity-60';
  return <header className={`sticky top-0 z-40 h-[72px] w-full transition-[transform,background-color,color,backdrop-filter,box-shadow] duration-[850ms] ease-silk will-change-transform motion-reduce:transition-none md:h-[92px] ${hidden ? '-translate-y-full' : 'translate-y-0'} ${solid ? 'bg-paper/85 text-ink shadow-[0_1px_0_rgba(0,0,0,0.06)] backdrop-blur-md' : 'bg-transparent text-paper shadow-none'}`} data-id="element-537" data-mp="35k7yluefwlup">
      <motion.div className="mx-auto grid h-full w-full max-w-[1328px] grid-cols-[1fr_auto_1fr] items-center px-6 md:px-8" initial={reduceMotion ? false : {
      opacity: 0,
      y: -8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 1.3,
      ease: [0.22, 1, 0.36, 1],
      delay: 0.4
    }} data-id="element-538" data-mp="1wd62aqebtobp">
        <nav aria-label="Primary" className="justify-self-start" data-id="element-539" data-mp="18m0lo90kas0c">
          <ul className="hidden items-center gap-8 lg:flex" data-id="element-540" data-mp="1cof5avc21vso">
            {headerNavLeft.map(item => <li key={item.label} data-id="element-541" data-mp="yp5s84j5bhsx">
                <a href={item.href} className={linkClass} data-id="element-542" data-mp="yq7b2v6el8l7">
                  {t(item.key)}
                </a>
              </li>)}
          </ul>
          <button type="button" onClick={onOpenMenu} className={`${linkClass} lg:hidden`} aria-haspopup="dialog" data-id="element-543" data-mp="2t36ochnb9qpb">
            {t('nav.menu')}
          </button>
        </nav>

        <a href="#top" aria-label={t('nav.home')} dir="ltr" data-id="element-544" data-mp="2xznmm4o85lg4">
          <Logo className="block text-[17px] md:text-[22px]" data-id="element-545" data-mp="mfg1wh7m1fcr" />
        </a>

        <nav aria-label="Secondary" className="flex items-center justify-self-end" data-id="element-546" data-mp="hg3dvt50628l">
          <ul className="hidden items-center gap-8 lg:flex" data-id="element-547" data-mp="w47r4hladljz">
            {headerNavRight.map(item => <li key={item.label} data-id="element-548" data-mp="rd7rh4ks8pds">
                <a href={item.href} className={linkClass} data-id="element-549" data-mp="rc6hz4jf0ay6">
                  {t(item.key)}
                </a>
              </li>)}
          </ul>
          <a href="/private-line" className={`${linkClass} lg:hidden`} data-id="element-550" data-mp="106ku4y8nf5d2">
            {t('nav.privateLine')}
          </a>
        </nav>
      </motion.div>
    </header>;
}